// src/utils/binaryMarker.js
//
// Pure helpers for the `__BINARY__:` body marker the proxy capture layer uses
// to flag binary/compressed bodies it couldn't decode to text
// (`proxy-server/decodeBody.js` emits `__BINARY__:<mime>;base64,<data>` — no
// `data:` URI infix).
//
// B11 (body-coverage-spec WP3): the frontend's HAR-import path
// (`pages/MainApp.js`) used to emit `__BINARY__:data:<mime>;base64,<data>` —
// an extra `data:` infix that doesn't match the capture format. Consumers
// that split on `;` to read the mime type (e.g. `unwrapBinary` in
// InspectraNetworkView.jsx) would then read `data:<mime>` as the type.
//
// `buildBinaryMarker` always emits the capture-format marker (no infix).
// `normalizeBinaryMarker`/`parseBinaryMarker` tolerate BOTH forms on read, so
// rows carrying the legacy (pre-fix) marker shape still render correctly.

export const BINARY_MARKER_PREFIX = '__BINARY__:';

/** Build a capture-format binary marker string: `__BINARY__:<mime>;base64,<data>`. */
export function buildBinaryMarker(mime, base64) {
  const type = mime || 'application/octet-stream';
  return `${BINARY_MARKER_PREFIX}${type};base64,${base64 || ''}`;
}

/** True when `value` is a string carrying a `__BINARY__:` marker (either form). */
export function isBinaryMarker(value) {
  return typeof value === 'string' && value.startsWith(BINARY_MARKER_PREFIX);
}

/**
 * Normalize a `__BINARY__:` marker to the capture-format shape, stripping a
 * legacy `data:` URI infix if present. Non-marker values pass through
 * unchanged (including non-strings).
 */
export function normalizeBinaryMarker(value) {
  if (!isBinaryMarker(value)) return value;
  const rest = value.slice(BINARY_MARKER_PREFIX.length);
  const normalizedRest = rest.startsWith('data:') ? rest.slice('data:'.length) : rest;
  return BINARY_MARKER_PREFIX + normalizedRest;
}

/**
 * Parse a `__BINARY__:` marker (either form) into `{ type, base64 }`.
 * Returns null for non-marker values.
 */
export function parseBinaryMarker(value) {
  if (!isBinaryMarker(value)) return null;
  const normalized = normalizeBinaryMarker(value);
  const rest = normalized.slice(BINARY_MARKER_PREFIX.length);
  const [meta, base64] = rest.split(',');
  const type = (meta || 'application/octet-stream').split(';')[0];
  return { type, base64: base64 || '' };
}

/**
 * One-line label for a binary body marker, e.g. "[Binary · protobuf — 178 bytes]".
 * The size is the DECODED byte count (base64 inflates by a third), and the kind
 * comes from the marker's mime: a protobuf SDK log is not "compressed".
 * Returns null for non-marker values.
 */
export function describeBinaryBody(value) {
  const parsed = parseBinaryMarker(value);
  if (!parsed) return null;
  const { type, base64 } = parsed;
  const clean = base64.replace(/\s+/g, '');
  const pad = clean.endsWith('==') ? 2 : clean.endsWith('=') ? 1 : 0;
  const bytes = Math.max(0, Math.floor((clean.length * 3) / 4) - pad);
  const t = String(type || '').toLowerCase();
  const kind = /protobuf/.test(t) ? 'protobuf'
    : /gzip|zip|brotli|compress|deflate/.test(t) ? 'compressed'
    : t.startsWith('image/') ? 'image'
    : t.startsWith('video/') || t.startsWith('audio/') ? 'media'
    : t && t !== 'application/octet-stream' ? t
    : 'binary';
  const size = bytes < 1024 ? `${bytes} bytes` : `${(bytes / 1024).toFixed(1)} KB`;
  return `[Binary · ${kind} — ${size}]`;
}
